import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Typography, DatePicker, Checkbox, Input, Button, Row, Col, Divider, message, Card, Space } from 'antd';
import { HomeOutlined, EnvironmentOutlined, SearchOutlined, CalendarOutlined, CheckCircleOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { 
  trackQuoteEvent, 
  trackConversion, 
  trackUserAction,
  trackServiceSelection,
  trackAddressSelection,
  trackError,
  ANALYTICS_EVENTS 
} from '../../utils/analytics';
import { trackCampaignQuote, trackCampaignAction, isFromCampaign, getCurrentCampaign } from '../../utils/utmTracking';
import { EXTERIOR_SERVICES, INTERIOR_SERVICES, DEFAULT_QUOTE_AMOUNT, CURRENCY } from '../../constants/services';
import { isValidEmail, isValidPhone, getFrequencyDisplayName } from '../../utils/helpers';
import { Header } from '../../components/common/layout';
import { loadGoogleMaps, isGoogleMapsReady } from '../../utils/googleMapsLoader';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;

const QuotePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { address: initialAddress = "", propertyData: initialPropertyData = { sqft: 6000, lotSize: 10000 } } = location.state || {};
  
  // Address input states
  const [address, setAddress] = useState(initialAddress);
  const [addressInput, setAddressInput] = useState('');
  const [propertyData, setPropertyData] = useState(initialPropertyData);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputPosition, setInputPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Quote form states
  const [frequency, setFrequency] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [selectedServices, setSelectedServices] = useState({});
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mapUrl, setMapUrl] = useState('');
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedQuoteId, setSubmittedQuoteId] = useState(null);

  // Calculate services
  const exteriorServices = EXTERIOR_SERVICES;
  const interiorServices = INTERIOR_SERVICES;
  const allServices = [...exteriorServices, ...interiorServices];

  // Initialize Google Maps Places API
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMaps();
        console.log('Google Maps loaded successfully');
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    };

    initializeGoogleMaps();
  }, []);

  // Address handling with Google Places API
  const handleAddressChange = useCallback((e) => {
    const value = e.target.value;
    setAddressInput(value);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (value.length > 2 && isGoogleMapsReady()) {
      debounceRef.current = setTimeout(() => {
        setIsLoading(true);
        
        const google = window.google;
        const service = new google.maps.places.AutocompleteService();
        const request = {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'us' }
        };
        
        service.getPlacePredictions(request, (predictions, status) => {
          setIsLoading(false);
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(prediction => prediction.description));
          } else {
            setSuggestions([]);
          }
        });
      }, 300);
    } else {
      setSuggestions([]);
    }
  }, []);

  const handleSuggestionClick = useCallback((suggestion) => {
    setAddress(suggestion);
    setAddressInput(suggestion);
    setSuggestions([]);
    trackAddressSelection(suggestion);
  }, []);

  const handleServiceToggle = useCallback((serviceId) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
    trackServiceSelection(serviceId, !selectedServices[serviceId]);
  }, [selectedServices]);

  const handleSubmitQuote = async () => {
    if (!address || !frequency || !startDate || !email || !phone) {
        message.error('Please fill in all required fields');
        return;
      }

    if (!isValidEmail(email)) {
      message.error('Please enter a valid email address');
        return;
      }

    if (!isValidPhone(phone)) {
      message.error('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);

    try {
      const quoteData = {
        // Contact Information
        email: email,
        phone: phone,
        
        // Property Information
        address: address,
        propertyData: propertyData,
        
        // Service Configuration
        serviceFrequency: frequency,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        
        // Selected Services
        selectedServices: selectedServices,
        exteriorServices: exteriorServices.filter(service => selectedServices[service.id]),
        interiorServices: interiorServices.filter(service => selectedServices[service.id]),
        
        // Quote Information
        estimatedAmount: DEFAULT_QUOTE_AMOUNT,
        currency: CURRENCY,
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Status
        status: 'pending',
        source: 'website_quote_form'
      };

      // Add campaign tracking if applicable
      if (isFromCampaign()) {
        const campaign = getCurrentCampaign();
        quoteData.campaign = campaign;
        trackCampaignQuote(campaign, quoteData);
      }

      const docRef = await addDoc(collection(db, 'quotes'), quoteData);
      setSubmittedQuoteId(docRef.id);
      setQuoteSubmitted(true);
      
      // Track successful quote submission
      trackQuoteEvent(ANALYTICS_EVENTS.QUOTE_SUBMIT, quoteData);
      trackConversion('quote_submitted', quoteData.estimatedAmount);
      
      message.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      trackError('quote_submission_failed', error);
      message.error('Failed to submit quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate map URL when address changes using Google Maps
  useEffect(() => {
    const geocodeAddress = () => {
      if (!address || !isGoogleMapsReady()) {
        return;
      }

      const google = window.google;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          
          // Create Google Maps embed URL
          const mapUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyDcSIgP5JIYkfFFcXiJhLRK879P6mblIjY&q=${encodeURIComponent(address)}&zoom=15`;
          setMapUrl(mapUrl);
        } else {
          console.error('Geocoding failed:', status);
        }
      });
    };

    if (address) {
      geocodeAddress();
    }
  }, [address]);

  const servicesDisabled = !frequency || !startDate;
  const isEmailValid = isValidEmail(email);
  const isPhoneValid = isValidPhone(phone);
  const canViewPrice = quoteSubmitted && isEmailValid && isPhoneValid;

  // Success Screen
  if (quoteSubmitted) {
    return (
      <Content style={{ 
          padding: '20px', 
        backgroundColor: '#f8f9fa', 
          minHeight: '100vh',
        fontFamily: theme.fonts.primary,
        marginTop: '64px'
      }}>
        <div style={{ 
            maxWidth: '600px', 
            margin: '0 auto', 
          backgroundColor: 'white', 
            padding: '40px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            textAlign: 'center'
        }}>
          <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '20px' }} />
          <Title level={2} style={{ marginBottom: '20px', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
            Quote Request Submitted!
          </Title>
          <Text style={{ color: theme.colors.gray, fontSize: '18px', fontFamily: theme.fonts.primary, marginBottom: '30px', display: 'block' }}>
            Thank you for choosing HSP! We've received your quote request and will contact you within 24 hours.
          </Text>
          <Space size="middle">
            <Button
              type="primary"
              size="large"
              onClick={() => window.location.href = '/'}
              style={{
                background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                border: 'none',
                borderRadius: '16px',
                height: '48px',
                padding: '0 32px',
                fontFamily: theme.fonts.primary,
                fontWeight: '700',
                fontSize: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Back to Home
            </Button>
            <Button
              size="large"
              onClick={() => {
                setQuoteSubmitted(false);
                setSubmittedQuoteId(null);
                setEmail('');
                setPhone('');
                setFrequency('');
                setStartDate(null);
                setSelectedServices({});
              }}
              style={{
                backgroundColor: 'white',
                borderColor: theme.colors.primary,
                color: theme.colors.primary,
                borderRadius: '16px',
                height: '48px',
                padding: '0 32px',
                fontFamily: theme.fonts.primary,
                fontWeight: '600'
              }}
            >
              Submit Another Quote
            </Button>
          </Space>
        </div>
      </Content>
    );
  }

  return (
    <>
      <Header />
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(253, 134, 92, 0.4);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(253, 134, 92, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(253, 134, 92, 0);
            }
          }
          
          .quote-step {
            animation: fadeInUp 0.6s ease-out;
          }
          
          .service-card {
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .service-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.1);
          }
          
          .service-card.selected {
            border-color: ${theme.colors.primary};
            background: linear-gradient(135deg, rgba(253, 134, 92, 0.05) 0%, rgba(253, 134, 92, 0.1) 100%);
          }
          
          .step-indicator {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: ${theme.colors.primary};
            color: white;
            font-weight: bold;
            font-size: 16px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .step-indicator.completed {
            background: #52c41a;
          }
          
          .step-indicator.current {
            background: linear-gradient(135deg, ${theme.colors.primary} 0%, #ff9a6b 100%);
          }
          
          .progress-bar {
            background: linear-gradient(90deg, ${theme.colors.primary} 0%, #ff9a6b 100%);
            height: 4px;
            border-radius: 2px;
            transition: width 0.3s ease;
          }
        `}
      </style>
      <Content style={{ 
        padding: '0', 
        backgroundColor: '#f8f9fa', 
          minHeight: '100vh',
          fontFamily: theme.fonts.primary,
        marginTop: '64px'
      }}>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #FD865C 0%, #ff9a6b 100%)',
          padding: '60px 20px',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Title level={1} style={{ 
              color: 'white',
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: '700',
              marginBottom: '16px',
            fontFamily: theme.fonts.primary
          }}>
              Get Your Free Quote
          </Title>
          <Text style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
              fontFamily: theme.fonts.primary,
              lineHeight: 1.6,
              maxWidth: '600px',
              margin: '0 auto',
              display: 'block'
            }}>
              Professional home services tailored to your needs. Get a personalized quote in minutes.
          </Text>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderBottom: '1px solid #e8e8e8'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className={`step-indicator ${address ? 'completed' : 'current'}`}>
                  {address ? '✓' : '1'}
                </div>
                <Text style={{ fontWeight: '600', fontSize: '14px' }}>Address</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className={`step-indicator ${frequency && startDate ? 'completed' : (address ? 'current' : '')}`}>
                  {frequency && startDate ? '✓' : '2'}
                </div>
                <Text style={{ fontWeight: '600', fontSize: '14px' }}>Schedule</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className={`step-indicator ${Object.keys(selectedServices).length > 0 ? 'completed' : (frequency && startDate ? 'current' : '')}`}>
                  {Object.keys(selectedServices).length > 0 ? '✓' : '3'}
                </div>
                <Text style={{ fontWeight: '600', fontSize: '14px' }}>Services</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className={`step-indicator ${email && phone ? 'completed' : (Object.keys(selectedServices).length > 0 ? 'current' : '')}`}>
                  {email && phone ? '✓' : '4'}
                </div>
                <Text style={{ fontWeight: '600', fontSize: '14px' }}>Contact</Text>
              </div>
            </div>
            <div style={{ 
              height: '4px',
              background: '#e8e8e8',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div 
                className="progress-bar"
                style={{
                  width: `${(() => {
                    let progress = 0;
                    if (address) progress += 25;
                    if (frequency && startDate) progress += 25;
                    if (Object.keys(selectedServices).length > 0) progress += 25;
                    if (email && phone) progress += 25;
                    return progress;
                  })()}%`
                }}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>

          {/* Step 1: Address Input */}
          {!address && (
            <div className="quote-step" style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <div className="step-indicator current">1</div>
                <div>
                  <Title level={4} style={{ margin: '0', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                    Where should we provide services?
                  </Title>
                  <Text style={{ color: theme.colors.gray, fontSize: '14px' }}>
                    Enter your property address to get started
                  </Text>
                </div>
              </div>
              
              <div style={{ position: 'relative' }}>
                <Input 
                  placeholder="123 Main Street, City, State 12345" 
                  size="large"
                  prefix={<HomeOutlined style={{ color: theme.colors.primary, fontSize: '18px' }} />}
                  suffix={isLoading ? <SearchOutlined style={{ color: theme.colors.primary }} spin /> : null}
                  style={{ 
                    height: '56px',
                    fontSize: '16px',
                    fontFamily: theme.fonts.primary,
                    borderRadius: '12px'
                  }} 
                  value={addressInput} 
                  onChange={handleAddressChange} 
                  ref={inputRef}
                />
                
                {/* Address Suggestions */}
                {suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                          fontFamily: theme.fonts.primary,
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        {suggestion}
              </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        )}

          {/* Step 1 Complete: Address Display */}
          {address && (
            <div className="quote-step" style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="step-indicator completed">✓</div>
                  <div>
                    <Title level={5} style={{ margin: '0', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                      Service Address
                    </Title>
                    <Text style={{ color: theme.colors.gray, fontSize: '14px' }}>
                      {address}
                    </Text>
                  </div>
                </div>
                <Button 
                  type="text" 
                  onClick={() => {
                    setAddress('');
                    setAddressInput('');
                    setSuggestions([]);
                    setIsLoading(false);
                    setMapUrl('');
                  }}
                  style={{ 
                    color: theme.colors.primary,
                    fontFamily: theme.fonts.primary,
                    fontWeight: '500'
                  }}
                >
                  Change Address
                </Button>
              </div>
              
              {/* Google Maps Display */}
              {mapUrl && (
                <div style={{
                  width: '100%',
                  height: '200px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e8e8e8'
                }}>
                  <iframe 
                    src={mapUrl}
                    width="100%" 
                    height="200" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy"
                    title="Property Location"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Schedule Selection */}
          {address && (
            <div className="quote-step" style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <div className={`step-indicator ${frequency && startDate ? 'completed' : 'current'}`}>
                  {frequency && startDate ? '✓' : '2'}
              </div>
                <div>
                  <Title level={4} style={{ margin: '0', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                    When would you like to start?
                  </Title>
                  <Text style={{ color: theme.colors.gray, fontSize: '14px' }}>
                    Choose your service frequency and start date
                  </Text>
              </div>
            </div>

              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '20px' }}>
                    <Text style={{ 
                      fontWeight: '600', 
                      marginBottom: '12px', 
                fontSize: '16px',
                color: theme.colors.black,
                      fontFamily: theme.fonts.primary,
                      display: 'block'
              }}>
                Service Frequency
                    </Text>
              <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '12px'
                    }}>
                      {[
                        { value: 'monthly', label: 'Monthly', desc: 'Every month' },
                        { value: '3months', label: '3 Months', desc: 'Quarterly' },
                        { value: '6months', label: '6 Months', desc: 'Twice yearly' },
                        { value: 'annual', label: 'Annual', desc: 'Once yearly' }
                      ].map((option) => (
                        <div
                          key={option.value}
                          className={`service-card ${frequency === option.value ? 'selected' : ''}`}
                          onClick={() => setFrequency(option.value)}
                    style={{ 
                            padding: '16px',
                            border: `2px solid ${frequency === option.value ? theme.colors.primary : '#e8e8e8'}`,
                            borderRadius: '12px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backgroundColor: frequency === option.value ? 'rgba(253, 134, 92, 0.05)' : 'white'
                          }}
                        >
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: '14px', 
                            color: theme.colors.black,
                  fontFamily: theme.fonts.primary,
                            marginBottom: '4px'
                          }}>
                            {option.label}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: theme.colors.gray,
                            fontFamily: theme.fonts.primary
                          }}>
                            {option.desc}
              </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '20px' }}>
                    <Text style={{ 
                      fontWeight: '600', 
                      marginBottom: '12px', 
                fontSize: '16px',
                color: theme.colors.black,
                      fontFamily: theme.fonts.primary,
                      display: 'block'
              }}>
                Start Date
                    </Text>
              <DatePicker 
                      size="large"
                      placeholder="Select start date"
                      value={startDate}
                      onChange={setStartDate}
                      suffixIcon={<CalendarOutlined style={{ cursor: 'pointer' }} />}
                      allowClear={false}
                      getPopupContainer={(trigger) => trigger.parentElement}
                style={{ 
                  width: '100%',
                        height: '48px',
                        fontFamily: theme.fonts.primary,
                        borderRadius: '12px'
                }}
                      disabledDate={(current) => {
                        // Quote page allows any date selection - no restrictions
                        return false;
                      }}
              />
            </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Step 3: Service Selection */}
          {address && frequency && startDate && (
            <div className="quote-step" style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <div className={`step-indicator ${Object.keys(selectedServices).length > 0 ? 'completed' : 'current'}`}>
                  {Object.keys(selectedServices).length > 0 ? '✓' : '3'}
                </div>
                <div>
                  <Title level={4} style={{ margin: '0', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                    What services do you need?
                </Title>
                  <Text style={{ color: theme.colors.gray, fontSize: '14px' }}>
                    Select the services you'd like us to perform
                  </Text>
                        </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px'
              }}>
                {allServices.map((service) => (
                  <div
                    key={service.id}
                    className={`service-card ${selectedServices[service.id] ? 'selected' : ''}`}
                    onClick={() => handleServiceToggle(service.id)}
                      style={{ 
                      padding: '20px',
                      border: `2px solid ${selectedServices[service.id] ? theme.colors.primary : '#e8e8e8'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      backgroundColor: selectedServices[service.id] ? 'rgba(253, 134, 92, 0.05)' : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Checkbox 
                        checked={selectedServices[service.id] || false}
                        onChange={() => handleServiceToggle(service.id)}
                        style={{ marginRight: '12px' }}
                      />
                      <Text style={{ 
                        fontWeight: '600', 
                        fontSize: '16px', 
                          color: theme.colors.black,
                          fontFamily: theme.fonts.primary
                        }}>
                          {service.name}
                      </Text>
                        </div>
                    <Text style={{ 
                          color: theme.colors.gray, 
                          fontSize: '14px', 
                      fontFamily: theme.fonts.primary,
                      lineHeight: 1.4
                        }}>
                          {service.description}
                    </Text>
                  </div>
                ))}
              </div>
                </div>
              )}
              
          {/* Step 4: Contact Information */}
          {address && frequency && startDate && Object.keys(selectedServices).length > 0 && (
            <div className="quote-step" style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <div className={`step-indicator ${email && phone ? 'completed' : 'current'}`}>
                  {email && phone ? '✓' : '4'}
                </div>
                <div>
                  <Title level={4} style={{ margin: '0', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                    How can we reach you?
                  </Title>
                  <Text style={{ color: theme.colors.gray, fontSize: '14px' }}>
                    We'll use this information to send you your quote
                </Text>
              </div>
            </div>

              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '20px' }}>
              <Text style={{ 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      fontSize: '14px',
                      color: theme.colors.black,
                      fontFamily: theme.fonts.primary,
                      display: 'block'
                    }}>
                      Email Address *
              </Text>
                  <Input
                      size="large"
                      placeholder="your@email.com"
                      prefix={<MailOutlined style={{ color: theme.colors.primary }} />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ 
                        height: '48px',
                        fontFamily: theme.fonts.primary,
                        borderRadius: '12px'
                      }}
                    />
                  </div>
                </Col>
                
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '20px' }}>
                    <Text style={{ 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      fontSize: '14px',
                      color: theme.colors.black,
                      fontFamily: theme.fonts.primary,
                      display: 'block'
                    }}>
                      Phone Number *
                    </Text>
                  <Input
                      size="large"
                      placeholder="(555) 123-4567"
                      prefix={<PhoneOutlined style={{ color: theme.colors.primary }} />}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    style={{ 
                        height: '48px',
                        fontFamily: theme.fonts.primary,
                        borderRadius: '12px'
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}

            {/* Submit Button */}
          {address && frequency && startDate && Object.keys(selectedServices).length > 0 && email && phone && (
            <div className="quote-step" style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8',
              textAlign: 'center'
            }}>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                onClick={handleSubmitQuote}
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '56px',
                  padding: '0 48px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: '700',
                  fontSize: '18px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)'
                }}
              >
                {isSubmitting ? 'Submitting Quote...' : 'Get My Free Quote'}
              </Button>
              <Text style={{ 
                display: 'block', 
                marginTop: '16px', 
              color: theme.colors.gray, 
              fontSize: '14px',
              fontFamily: theme.fonts.primary
            }}>
                Your quote will be delivered within 24 hours
              </Text>
            </div>
          )}

        </div>
    </Content>
    </>
  );
};

export default QuotePage;